import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGlobal } from '../../context/GlobalContext';
import { getContentFontStyle } from '../../lib/contentBlockFontSize';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getIconFallbackLabel, getUiText } from '../../lib/uiText';
import { resolveUrlForLocale } from '../../lib/localizedSlugs';
import maximizeIcon from '../../assets/icons/icon_flecha_maximizar.svg';
import styles from './GalleryInfo.module.css';

gsap.registerPlugin(ScrollTrigger);

const GalleryInfo = ({ data }) => {
  const { locale } = useGlobal();
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxClosing, setLightboxClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const contentRef = useRef(null);
  const sliderRef = useRef(null);
  const slidesRef = useRef([]);
  const lightboxRef = useRef(null);
  const intervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const hasInitialized = useRef(false);
  const prevSlide = useRef(0);

  const items = data?.items || [];
  const total = items.length;
  const speed = data?.autoplaySpeed || 8000;
  const lightboxImage = lightbox !== null
    ? getStrapiMediaAttributes(items[lightbox]?.image, {
      altFallback: items[lightbox]?.title || items[lightbox]?.subtitle || data?.title || '',
      titleFallback: items[lightbox]?.title || items[lightbox]?.subtitle || data?.title || '',
    })
    : null;

  /* ── Viewport observer — autoplay only when fully visible ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Scroll-triggered entrance animations ── */
  useEffect(() => {
    if (!sectionRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top 85%',
        once: true,
      },
    });

    if (titleRef.current) {
      tl.fromTo(
        titleRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' }
      );
    }
    if (subtitleRef.current) {
      tl.fromTo(
        subtitleRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.3'
      );
    }
    if (contentRef.current) {
      tl.fromTo(
        contentRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
        '-=0.3'
      );
    }

    return () => tl.kill();
  }, []);

  /* ── Position slides vertically ── */
  const positionSlides = useCallback(
    (center, animate) => {
      if (total === 0) return;
      const dur = animate ? 0.7 : 0;

      for (let i = 0; i < total; i++) {
        const el = slidesRef.current[i];
        if (!el) continue;

        if (i === center) {
          gsap.to(el, {
            yPercent: 0,
            opacity: 1,
            zIndex: 3,
            duration: dur,
            ease: 'power2.inOut',
          });
        } else if (i < center) {
          gsap.to(el, {
            yPercent: -110,
            opacity: 0,
            zIndex: 1,
            duration: dur,
            ease: 'power2.inOut',
          });
        } else {
          gsap.to(el, {
            yPercent: 110,
            opacity: 0,
            zIndex: 1,
            duration: dur,
            ease: 'power2.inOut',
          });
        }
      }
    },
    [total]
  );

  /* ── Init slides (no animation) ── */
  useEffect(() => {
    if (total > 0 && !hasInitialized.current) {
      positionSlides(0, false);
      hasInitialized.current = true;
    }
  }, [total, positionSlides]);

  /* ── Animate slides + text on change ── */
  useEffect(() => {
    if (!hasInitialized.current) return;
    positionSlides(current, true);

    /* Animate text content change */
    const els = [subtitleRef.current, contentRef.current].filter(Boolean);
    if (els.length && current !== prevSlide.current) {
      const tl = gsap.timeline();
      tl.to(els, {
        y: -20,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        stagger: 0.05,
      });
      tl.call(() => {
        prevSlide.current = current;
      });
      tl.fromTo(
        els,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out', stagger: 0.1 }
      );
    }
  }, [current, positionSlides]);

  /* ── Autoplay — only when in viewport ── */
  useEffect(() => {
    if (total <= 1 || isPaused || !isInView) return;
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, speed);
    return () => clearInterval(intervalRef.current);
  }, [current, total, speed, isPaused, isInView]);

  /* ── Lightbox open with fade ── */
  const openLightbox = (idx) => {
    setIsPaused(true);
    clearInterval(intervalRef.current);
    setLightbox(idx);
    setLightboxClosing(false);
  };

  /* ── Lightbox close with fade ── */
  const closeLightbox = useCallback(() => {
    if (lightboxClosing) return;
    setLightboxClosing(true);
    const el = lightboxRef.current;
    if (el) {
      gsap.to(el, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => {
          setLightbox(null);
          setLightboxClosing(false);
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
        },
      });
    } else {
      setLightbox(null);
      setLightboxClosing(false);
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
    }
  }, [lightboxClosing]);

  /* ── Cleanup ── */
  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(resumeTimeoutRef.current);
    },
    []
  );

  /* ── Keyboard ── */
  useEffect(() => {
    if (lightbox === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    if (lightbox === null) return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [lightbox]);

  /* ── Arrow navigation ── */
  const goNext = () => {
    if (total <= 1) return;
    setIsPaused(true);
    clearInterval(intervalRef.current);
    setCurrent((prev) => (prev + 1) % total);
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 5000);
  };

  const goPrev = () => {
    if (total <= 1) return;
    setIsPaused(true);
    clearInterval(intervalRef.current);
    setCurrent((prev) => (prev - 1 + total) % total);
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 5000);
  };

  if (!items.length) return null;

  const currentItem = items[current] || {};

  /* ── Render content blocks (paragraph or list) ── */
  const renderContent = (blocks) => {
    if (!blocks || !blocks.length) return null;
    return blocks.map((block, idx) => {
      if (block.__component === 'shared.paragraph') {
        const paragraphStyle = getContentFontStyle(block?.fontSize, '1em');
        return (
          <p key={`p-${idx}`} className={styles.gi__text} style={paragraphStyle}>
            {block.text}
          </p>
        );
      }
      if (block.__component === 'shared.list') {
        const listTitleStyle = getContentFontStyle(block?.fontSize, '0.95em');
        const listItemStyle = getContentFontStyle(block?.fontSize, '1em');
        const ListTag = block.ordered ? 'ol' : 'ul';
        return (
          <div key={`list-wrap-${idx}`} className={styles.gi__listWrap}>
            {block.title && (
              <p className={styles.gi__listTitle} style={listTitleStyle}>{block.title}</p>
            )}
            <ListTag className={styles.gi__list}>
              {(block.items || []).map((li, liIdx) => (
                <li key={`li-${liIdx}`} className={styles.gi__listItem} style={listItemStyle}>
                  {li.text}
                </li>
              ))}
            </ListTag>
          </div>
        );
      }
      return null;
    });
  };

  /* ── Render section button (CTA1) ── */
  const renderButton = (button) => {
    if (!button || !button.label || !button.url) return null;

    const alignment = button.alignment || 'left';
    const isExternal = /^https?:\/\//i.test(button.url) || button.openInNewTab;
    const resolvedUrl = resolveUrlForLocale(button.url, locale);
    const iconMedia = button?.icon?.url
      ? getStrapiMediaAttributes(button.icon, {
        altFallback: getIconFallbackLabel(locale, 'button', button.label),
        titleFallback: getIconFallbackLabel(locale, 'button', button.label),
      })
      : null;

    const btnClass = [
      styles.gi__btn,
      button.darkText ? styles['gi__btn--dark'] : '',
    ].filter(Boolean).join(' ');

    const wrapClass = [
      styles.gi__btnWrap,
      styles[`gi__btnWrap--${alignment}`],
    ].join(' ');

    const content = (
      <>
        {iconMedia?.src && (
          <img
            src={iconMedia.src}
            alt={iconMedia.alt}
            title={iconMedia.title}
            className={styles.gi__btnIcon}
            aria-hidden="true"
          />
        )}
        <span>{button.label}</span>
      </>
    );

    return (
      <div className={wrapClass}>
        {isExternal ? (
          <a
            href={resolvedUrl}
            className={btnClass}
            target={button.openInNewTab ? '_blank' : undefined}
            rel={button.openInNewTab ? 'noopener noreferrer' : undefined}
            title={button.label}
          >
            {content}
          </a>
        ) : (
          <Link to={resolvedUrl} className={btnClass} title={button.label}>
            {content}
          </Link>
        )}
      </div>
    );
  };

  return (
    <section ref={sectionRef} className={styles.gi}>
      <div className={styles.gi__info}>
        {data?.title && (
          <h2 ref={titleRef} className={styles.gi__title}>
            {data.title}
          </h2>
        )}

        <div className={styles.gi__dynamic}>
          <div ref={subtitleRef} className={styles.gi__subtitle}>
            {currentItem.subtitle || ''}
          </div>

          <div ref={contentRef} className={styles.gi__content}>
            {renderContent(currentItem.content)}
            {renderButton(currentItem.button)}
          </div>
        </div>
      </div>

      <div className={styles.gi__sliderArea}>
        <div ref={sliderRef} className={styles.gi__slider}>
          {items.map((item, i) => {
            const imageMedia = getStrapiMediaAttributes(item?.image, {
              altFallback: item?.title || item?.subtitle || data?.title || '',
              titleFallback: item?.title || item?.subtitle || data?.title || '',
            });
            if (!imageMedia.src) return null;

            return (
              <div
                key={`gi-slide-${i}`}
                ref={(el) => (slidesRef.current[i] = el)}
                className={styles.gi__slide}
                onClick={() => openLightbox(i)}
              >
                <img
                  src={imageMedia.src}
                  alt={imageMedia.alt}
                  title={imageMedia.title}
                  className={styles.gi__image}
                  draggable={false}
                />
                <img
                  src={maximizeIcon}
                  alt={getUiText(locale, 'maximize')}
                  title={getUiText(locale, 'maximize')}
                  className={styles.gi__maximizeIcon}
                  draggable={false}
                />
              </div>
            );
          })}

          {total > 1 && (
            <div className={styles.gi__nav}>
              <button
                type="button"
                className={`${styles.gi__arrow} ${styles['gi__arrow--up']}`}
                onClick={goPrev}
                aria-label={getUiText(locale, 'previousImage')}
              >
                <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                  <path d="M1 11L10 2L19 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className={styles.gi__dots}>
                {items.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`${styles.gi__dot} ${
                      i === current ? styles['gi__dot--active'] : ''
                    }`}
                    onClick={() => setCurrent(i)}
                    aria-label={getUiText(locale, 'imageIndex', { index: i + 1 })}
                  />
                ))}
              </div>

              <button
                type="button"
                className={`${styles.gi__arrow} ${styles['gi__arrow--down']}`}
                onClick={goNext}
                aria-label={getUiText(locale, 'nextImage')}
              >
                <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                  <path d="M1 1L10 10L19 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && createPortal(
        <div
          ref={lightboxRef}
          className={styles.gi__lightbox}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={items[lightbox]?.title || items[lightbox]?.subtitle || data?.title || getUiText(locale, 'expandedImage')}
        >
          <button
            type="button"
            className={styles.gi__lightboxClose}
            onClick={closeLightbox}
            aria-label={getUiText(locale, 'close')}
          >
            &times;
          </button>
          <div
            className={styles.gi__lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage?.src}
              alt={lightboxImage?.alt || ''}
              title={lightboxImage?.title}
              className={styles.gi__lightboxImage}
            />
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default GalleryInfo;
